import type {
  AdminAuthenticatedSession,
  AdminCurrentSession,
  AdminForgotPasswordResponse,
  AdminForgotPasswordResult,
  AdminLoginResponse,
  AdminMeResponse,
  AdminOtpChallenge,
  AdminOtpResendResponse,
  AdminOtpVerifyResponse,
  AdminResetPasswordResponse,
  AdminResetPasswordResult,
} from '@mds/shared/types/admin-auth';

import { api } from '@/lib/api';

/**
 * The admin authentication endpoints, typed against the contracts the backend
 * serialises from — see `backend/docs/admin-auth-api.md`.
 *
 * Every call unwraps the `{ data }` envelope so callers deal in the payload.
 * The session itself is an HTTP-only cookie the browser handles; nothing here
 * ever sees a token.
 */

/** Step one: credentials in, OTP challenge out. Never confirms an account exists. */
export const login = (email: string, password: string): Promise<AdminOtpChallenge> =>
  api.post<AdminLoginResponse>('admin/auth/login', { email, password }).then((r) => r.data);

/** Step two: the emailed code. On success the session cookie is set. */
export const verifyOtp = (challengeId: string, code: string): Promise<AdminAuthenticatedSession> =>
  api
    .post<AdminOtpVerifyResponse>('admin/auth/otp/verify', { challengeId, code })
    .then((r) => r.data);

/** Rotates the code and extends expiry, keeping the attempt count. */
export const resendOtp = (challengeId: string): Promise<AdminOtpChallenge> =>
  api.post<AdminOtpResendResponse>('admin/auth/otp/resend', { challengeId }).then((r) => r.data);

/** Who the cookie belongs to, with current roles and permissions. 401 when signed out. */
export const getSession = (): Promise<AdminCurrentSession> =>
  api.get<AdminMeResponse>('admin/auth/me').then((r) => r.data);

/** Idempotent: a missing or already-revoked session still clears the cookie. */
export const logout = (): Promise<null> => api.post<null>('admin/auth/logout');

/** Always the same reply, whether or not the account exists. */
export const forgotPassword = (email: string): Promise<AdminForgotPasswordResult> =>
  api
    .post<AdminForgotPasswordResponse>('admin/auth/forgot-password', { email })
    .then((r) => r.data);

/** Consumes the emailed token and revokes every existing session. */
export const resetPassword = (token: string, password: string): Promise<AdminResetPasswordResult> =>
  api
    .post<AdminResetPasswordResponse>('admin/auth/reset-password', { token, password })
    .then((r) => r.data);
